import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';

interface CompanyLogoProps {
  src: string;
  /** Caps the logo height; width scales to preserve aspect ratio. Default 32. */
  maxHeight?: number;
  alt?: string;
}

/**
 * Renders a customer's uploaded company logo. Company logos are brand assets
 * designed for a light background, so in dark mode we do NOT recolor/invert them
 * (that would distort brand colors unpredictably) — instead we sit the logo on a
 * light "plate" so it renders as intended. In light mode the plate is absent, so
 * the appearance is unchanged. For pixel-perfect brand fidelity a future
 * enhancement can add an optional dark-mode logo upload and prefer it here.
 */
export default function CompanyLogo({
  src,
  maxHeight = 32,
  alt = 'Company logo',
}: CompanyLogoProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Box
      data-test-id="company-logo-plate"
      sx={{
        alignItems: 'center',
        display: 'inline-flex',
        ...(isDark
          ? {
              backgroundColor: theme.palette.common.white,
              borderRadius: 0.75,
              px: 0.75,
              py: 0.25,
            }
          : {}),
      }}
    >
      <img
        // max constraints with auto dimensions keep the aspect ratio while
        // capping full-resolution logos to the toolbar height
        style={{
          display: 'block',
          height: 'auto',
          maxHeight,
          maxWidth: '100%',
          width: 'auto',
        }}
        alt={alt}
        loading="lazy"
        src={src}
      />
    </Box>
  );
}
